import React from "react";
import {EngineeringOutlined, GavelOutlined, MoveToInboxOutlined, RequestQuoteOutlined,
        ScienceOutlined} from "@mui/icons-material";
/**
 * What a project holds, and what each kind is called.
 *
 * MOVED OUT OF Projects.tsx ON 2026-10-27, when a project became the eighteenth species and its
 * two bespoke pages were deleted. The vocabulary outlived them: the member panes on the standard
 * record page need it, and so does anything that reports a count per kind.
 *
 * THE VALUE IS A LOGICAL OBJECT FOR EVERY KIND, since 2026-10-27. A QuoteRequest used to be the
 * exception -- "a plain row, whose own USID is therefore the stable identifier" -- and it is the
 * nineteenth species now, so the membership means one thing whatever the kind. Keyed on the logical
 * object so that revising a quote does not move it out of its folder -- the cost Phase 9 recorded
 * when a contract line was keyed on a revision.
 */
export const MEMBER_KINDS = ["Product", "QuoteRequest", "PreparedQuote", "Contract",
                             "BuildPlan"] as const;

/**
 * A PRODUCT IS AN ITEM OF TYPE PRT-, since 2026-10-29 (Ed: "retire Product as its own Object/Object
 * Revision ... replace it with an Item with Object Type = PRT-"). The kind "Product" in a project
 * is therefore a filing of an Item, and the two facts below are what the screens need to make
 * one: which classification to create, and which type it must be.
 */
export const PRODUCT_TYPE_ACRONYM = "PRT-";

/** The classification each kind files, for the create dialog and the record page's filing control. */
export const KIND_CLASSIFICATION: Record<string, string> = {
    Product: "Item", QuoteRequest: "QuoteRequest", PreparedQuote: "PreparedQuote",
    Contract: "Contract", BuildPlan: "BuildPlan",
};

/** The kind a record is fileable as, from its classification and its type's acronym, or null. */
export function fileableKind(classification?: string | null, typeAcronym?: string | null): string | null {
    if (classification === "Item") return typeAcronym === PRODUCT_TYPE_ACRONYM ? "Product" : null;
    for (const [kind, held] of Object.entries(KIND_CLASSIFICATION)) {
        if (held === classification && kind !== "Product") return kind;
    }
    return null;
}

/** Each kind's icon, for the rail, the cards and the lanes. The four species' are their
 *  registry icons; a product's is its own, since no classification carries it any more. */
export const KIND_ICON: Record<string, React.ElementType> = {
    Product: ScienceOutlined, QuoteRequest: MoveToInboxOutlined, PreparedQuote: RequestQuoteOutlined,
    Contract: GavelOutlined, BuildPlan: EngineeringOutlined,
};

export type MemberKind = typeof MEMBER_KINDS[number];

/** Plural, for a tab. */
export const KIND_LABEL: Record<string, string> = {
    Product: "Products", QuoteRequest: "Quote Requests", PreparedQuote: "Quote Responses",
    Contract: "Contracts", BuildPlan: "Build Plans",
};

/** Singular and lower case, for "New quote response". */
export const KIND_ONE: Record<string, string> = {
    Product: "product", QuoteRequest: "quote request", PreparedQuote: "quote response",
    Contract: "contract", BuildPlan: "build plan",
};

/** A single value, a list, or nothing, as a list. The platform sends one row unwrapped. */
export function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

/** A timestamp as a date, or an em dash. */
export function when(value?: string | null): string {
    if (!value) return "—";
    const parsed = new Date(String(value).replace(" ", "T"));
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString();
}
